package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.MyStatsDto
import com.qrkontaq.data.dto.MyWorkOrderDto
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.tenant.MenuIcons
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px

/**
 * Personal overview for a cook or a waiter: only what this staff member
 * did themselves — cooked tickets for the kitchen, served tables for the floor.
 */
@Composable
fun StaffOverviewTab(
    repository: AdminRepository,
    rid: String,
    role: String,
) {
    var period by remember(rid) { mutableStateOf(StatsPeriod.DAY) }
    var offset by remember(rid) { mutableStateOf(0) }
    var stats by remember(rid) { mutableStateOf<MyStatsDto?>(null) }

    LaunchedEffect(rid, period, offset) {
        stats = null
        val (from, to) = rangeFor(period, offset)
        stats = runCatching { repository.myStats(rid, from, to) }.getOrNull()
            ?: MyStatsDto(from = from, to = to)
    }

    PeriodBar(
        period = period,
        offset = offset,
        onPeriod = { period = it; offset = 0 },
        onOffset = { offset = it },
    )

    val data = stats ?: return
    // A bartender mirrors the cook — what they prepared themselves — but for drinks.
    // A waiter sees the orders they personally created (took), so a shift with several
    // waiters can attribute each ticket to whoever opened it.
    val bartender = role == "BARTENDER"
    val cook = role == "COOK" || bartender
    val work = if (cook) data.cooked else data.taken

    // Summary tiles
    val dishCount = work.sumOf { order -> order.items.sumOf { it.qty } }
    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(12.px).margin(bottom = 14.px)) {
        StatTile(
            when {
                bartender -> AdminIcons.Martini
                cook -> AdminIcons.ChefHat
                else -> AdminIcons.QrCode
            },
            if (cook) AdminT.statCookedOrders else AdminT.statServedOrders,
            work.size.toString(),
            "#0F9D6E",
            Brand.GreenSoft,
        )
        StatTile(
            if (bartender) AdminIcons.Martini else MenuIcons.Utensils,
            when {
                bartender -> AdminT.statDrinksMade
                cook -> AdminT.statCookedDishes
                else -> AdminT.statServedDishes
            },
            dishCount.toString(),
            "#E8A33D",
            Color.rgba(232, 163, 61, 0.14f),
        )
        if (!cook) {
            StatTile(
                AdminIcons.Users,
                AdminT.statTablesServed,
                work.distinctBy { it.tableLabel }.size.toString(),
                "#E05B4C",
                Brand.CoralSoft,
            )
        }
    }

    if (work.isEmpty()) {
        AdminCard {
            AdminEmpty(AdminIcons.Dashboard, AdminT.myWorkEmpty, "")
        }
        return
    }

    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(14.px)) {
        // Dish breakdown: how many of each dish went through this person's hands.
        Column(Modifier.styleModifier { property("flex", "1"); property("min-width", "260px") }) {
            AdminCard {
                AdminTitle(
                    when {
                        bartender -> AdminT.myDrinksMade
                        cook -> AdminT.myDishesCooked
                        else -> AdminT.myDishesServed
                    },
                )
                val byDish = work
                    .flatMap { it.items }
                    .groupBy { it.name }
                    .map { (name, items) -> name to items.sumOf { it.qty } }
                    .sortedByDescending { it.second }
                val maxQty = byDish.maxOf { it.second }.coerceAtLeast(1)
                byDish.forEach { (name, qty) ->
                    Row(
                        Modifier.fillMaxWidth().margin(bottom = 10.px),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(Modifier.styleModifier { property("flex", "1") }.margin(right = 10.px)) {
                            SpanText(
                                name,
                                Modifier.fontSize(13.px).fontWeight(FontWeight.Medium)
                                    .color(Brand.Charcoal).attrsModifier { classes("qk-clamp1") },
                            )
                            Box(
                                Modifier
                                    .fillMaxWidth()
                                    .height(5.px)
                                    .margin(top = 4.px)
                                    .borderRadius(999.px)
                                    .backgroundColor(Brand.WarmBackground),
                            ) {
                                Box(
                                    Modifier
                                        .height(5.px)
                                        .borderRadius(999.px)
                                        .styleModifier {
                                            property("width", "${(100.0 * qty / maxQty).coerceAtLeast(4.0)}%")
                                            property("background", "linear-gradient(90deg,#0F9D6E,#17B981)")
                                        },
                                )
                            }
                        }
                        SpanText(
                            "×$qty",
                            Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                        )
                    }
                }
            }
        }

        // Timeline: every ticket with its table, dishes and the exact time.
        Column(Modifier.styleModifier { property("flex", "1"); property("min-width", "260px") }) {
            AdminCard {
                AdminTitle(AdminT.myTimelineTitle)
                work.forEach { order -> TimelineEntry(order, period) }
            }
        }
    }
}

@Composable
private fun TimelineEntry(order: MyWorkOrderDto, period: StatsPeriod) {
    Row(
        Modifier
            .fillMaxWidth()
            .margin(bottom = 8.px)
            .borderRadius(12.px)
            .padding(10.px)
            .backgroundColor(Brand.WarmBackground),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.styleModifier { property("flex", "1") }.margin(right = 10.px)) {
            SpanText(
                order.items.joinToString(", ") { if (it.qty > 1) "${it.name} ×${it.qty}" else it.name },
                Modifier.fontSize(13.px).fontWeight(FontWeight.Medium).color(Brand.Charcoal),
            )
            Row(Modifier.margin(top = 3.px), verticalAlignment = Alignment.CenterVertically) {
                InlineIcon(AdminIcons.QrCode, 11, "#6B7280")
                SpanText(
                    AdminT.tableN(order.tableLabel),
                    Modifier.fontSize(11.px).color(Brand.TextSecondary).margin(left = 5.px),
                )
            }
        }
        SpanText(
            workTime(order.at, period),
            Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(Brand.TextSecondary)
                .attrsModifier { classes("qk-nowrap") },
        )
    }
}

/** "14:05" inside a single day, "31.07 14:05" for longer ranges. */
private fun workTime(at: String, period: StatsPeriod): String {
    if (at.isBlank()) return ""
    val d = kotlin.js.Date(at)
    val hm = "${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}"
    if (period == StatsPeriod.DAY) return hm
    val dm = "${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}"
    return "$dm $hm"
}
